import React, { useEffect, useCallback, useState } from 'react';

import {
    Box,
    Divider,
    Typography,
    Button,
    Dialog,
    TextField,
    InputAdornment,
    FormControl,
    Grid,
    InputLabel,
    Select,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Switch,
    styled,
    Chip,
    ListItemText,
    OutlinedInput,
    Checkbox,
    List,
    ListItem,
    Tooltip,
    IconButton
} from '@mui/material';

import { useFormik } from 'formik';
import { useAppContextFeatures } from '../../../context/AppContext';

import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import CommonRestricted from '../../common/CommonRestricted';

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useItems, itemTypes, targetTypes } from '../../../hooks/api/useItems';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';

import { mergeSchema, checkValueExists } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString"

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";

import {
    validationSchema,
    initialValues,
    validationSchemaRows,
    initialValuesRow
} from '../../../schema/products/itemSchema';

import {
    inputTypes,
    inputTypesDef
} from '../../../schema/customFields/customFieldSchema'

export default function UpdateItem(
    {
        routeType,
        id,
        handleOnSuccess,
        dataUoms,
        dataSuppliers
    }
) {

    const [savedValues, setSavedValues] = useState()
    const { promptMessage } = useSystemPrompt()
    const { update, get: getItem, filterOperatorTypes } = useItems({ itemType: itemTypes.item, targetType: targetTypes.item });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.item, customFieldFunction: customFieldFunctions.item.item })

    const { setSystemLoading } = useAppContextFeatures()
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.item.id)

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.item,
            moduleFunction: customFieldFunctions.item.item
        }
    )

    const [dataProduct, setDataProduct] = useState()
    const [dataItem, setDataItem] = useState()

    const [customFields, setCustomFields] = useState(dataCustomFields)

    const getSchema = () => {

        return mergeSchema(validationSchema, validationSchemaRows)
    }

    const loadData = async (id) => {

        setSystemLoading(true);

        try {

            const { data } = await getItem({ id, dataTypesArray: ["all_supplier"] })

            const {
                item,
                custom_fields: savedCustomFields = []
            } = data;

            const {
                all_supplier: allSuppliers = []
            } = item;

            const cf = await getInitialFormStateRows({
                savedCustomFields,
            })

            let supplierId = allSuppliers.length ? allSuppliers.map(i => i.id) : []

            // console.log('supplierId', supplierId, allSuppliers)

            setSavedValues(prev => ({
                ...prev,
                uomId: item?.uom_id ? item.uom_id : "",
                code: item?.code ? item.code : "",
                supplierId,

                uomDesc: item?.item_description ? item.item_description : "",
                remark: item?.remark ? item.remark : "",
                repackRemark: item?.repack_remark ? item.repack_remark : "",
                cost: item?.cost ? item.cost : "",
                uomPrice: item?.uom_price ? item.uom_price : "",
                floorPrice: item?.floor_price ? item.floor_price : "",
                volumeWeight: item?.volume_weight ? item.volume_weight : "",
                grossWeight: item?.gross_weight ? item.gross_weight : "",
                netWeight: item?.net_weight ? item.net_weight : "",
                width: item?.dimension_width ? item.dimension_width : "",
                height: item?.dimension_height ? item.dimension_height : "",
                length: item?.dimension_length ? item.dimension_length : "",
                uomStatus: item?.status === "1",
                packageStatus: item?.is_package === "1",
                cbm: item?.cbm ? item.cbm : "",
                transformRatio: Number(item?.transform_ratio),
                relocateTopUpQty: item?.relocate_topup_qty ? item.relocate_topup_qty : "",
                reorderTopUpQty: item?.reorder_topup_qty ? item.reorder_topup_qty : "",
                relocateThresholdQty: item?.relocate_threshold_qty ? item.relocate_threshold_qty : "",
                reorderThresholdQty: item?.reorder_threshold_qty ? item.reorder_threshold_qty : "",
                rows: cf
            }))

            // get the product
            if (item?.product_id) {
                const _dataProduct = await fetchProduct({ productId: item?.product_id })

                // console.log('_dataProduct, _dataProduct', _dataProduct);

                if (_dataProduct?.default_uom_item_id) {
                    await fetchItem({ itemId: _dataProduct.default_uom_item_id })
                }

                if (_dataProduct?.default_uom_item_id === id) { // if default uom item
                    setFieldValue(`transformRatio`, 1)
                }
            }

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const fetchItem = async ({ itemId }) => {

        try {

            const { data } = await getItem({ id: itemId, itemType: itemTypes.item })

            setDataItem(data?.item)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchProduct = async ({ productId }) => {

        try {

            const { data } = await getItem({ id: productId, itemType: itemTypes.product })

            setDataProduct(data?.product)

            return data?.product

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        return false;
    }

    useEffect(() => {
        loadData(id)
    }, [])

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try {

            // custom fields
            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ customFields, rows })

            let data = {
                item: {
                    code: currentValues?.code,
                    uom_id: currentValues?.uomId,
                    is_package: (currentValues.packageStatus) ? 1 : 0,
                    status: (currentValues.uomStatus) ? 1 : 0,

                    item_description: currentValues?.uomDesc,
                    remark: currentValues?.remark,
                    cost: currentValues?.cost,
                    uom_price: currentValues?.uomPrice,
                    floor_price: currentValues?.floorPrice,

                    gross_weight: currentValues?.grossWeight,
                    volume_weight: currentValues?.volumeWeight,
                    net_weight: currentValues?.netWeight,
                    dimension_width: currentValues?.width,
                    dimension_height: currentValues?.height,

                    dimension_length: currentValues?.length,
                    cbm: currentValues?.cbm,
                },
                props: {
                    repack_remark: currentValues?.repackRemark,
                    supplier_id: currentValues?.supplierId,
                    transform_ratio: currentValues?.transformRatio,
                    relocate_topup_qty: currentValues?.relocateTopUpQty,
                    reorder_topup_qty: currentValues?.reorderTopUpQty,
                    relocate_threshold_qty: currentValues?.relocateThresholdQty,
                    reorder_threshold_qty: currentValues?.reorderThresholdQty
                },
                custom_fields: cf
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)

            if (handleOnSuccess)
                handleOnSuccess();

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const formik = useFormik({
        // initialValues: getInitialValues(),
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;

    const RenderCustomFieldSelection = () => {

        if (!values?.rows) return <></>

        const rows = values.rows

        if (values.rows.length <= 0) return <></>

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title='Custom Properties'
                        subTitle={"Provide custom item information"}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            values.rows.map((item, index) => {

                                const { rows } = values

                                let itemValues = rows.length ? rows[index] : {}

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                            // error: Boolean(itemErrors?.customValue),
                                                            // helperText: itemErrors?.customValue
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        customFields,
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        customFields,
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    // const RenderCustomFieldSelection = () => {

    //     if(!values?.rows) return <></>

    //     const rows = values.rows

    //     return (
    //     <>
    //         <Grid item xs={12}>
    //             <Divider sx={{ mt: 2, mb: 1 }}/>
    //         </Grid>

    //         <Grid item xs={12} sm={12} md={3} textAlign={"left"}>  
    //             <CommonSectionHeader
    //                 title='Custom Properties'
    //                 subTitle={"Provide custom item information"}
    //             />
    //         </Grid>
    //         <Grid item xs={12} sm={12} md={9}>
    //             <List>
    //             {
    //                 rows && rows.length > 0
    //                 && rows.map((item, index) => {

    //                     const itemValues = item;
    //                     let itemTouched = touched?.rows
    //                     let itemErrors = errors?.rows
    //                     // const itemTouched = touched?.rows

    //                     if(itemTouched){
    //                         itemTouched = (itemTouched[index] !== undefined)? itemTouched[index]: {};
    //                     }

    //                     if(itemErrors){
    //                         itemErrors = (itemErrors[index] !== undefined)? itemErrors[index]: {};
    //                     }
    //                     return (
    //                         <ListItem 
    //                             key={index} 
    //                             divider 
    //                             disablePadding
    //                             sx={{
    //                                 py: 1
    //                             }}
    //                         >
    //                             <Grid key={index} container spacing={1}>
    //                                 <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
    //                                     {/* {
    //                                         RenderCustomFieldSelect({
    //                                             index,
    //                                             itemValues,
    //                                             itemErrors,
    //                                             itemTouched
    //                                         })
    //                                     } */}

    //                                     <Chip 
    //                                         label={itemValues.fieldLabel} 
    //                                         color='primary' 
    //                                         variant='outlined'
    //                                         size='large'
    //                                         // avatar={<CommonIcons name={icons.add} />}
    //                                         sx={{
    //                                             width: '100%',
    //                                             // textAlign: 'left',
    //                                             fontSize: 16,
    //                                             fontWeight: 800
    //                                         }}
    //                                     />

    //                                 </Grid>
    //                                 <Grid item md={9} xs={12} sm={12}>

    //                                     {
    //                                         getCustomFieldType(itemValues.customFieldId) === inputTypesDef.textbox &&

    //                                         RenderCustomFieldSelectionTextbox({
    //                                             index,
    //                                             itemValues,
    //                                             itemErrors,
    //                                             itemTouched
    //                                         })
    //                                     }

    //                                     {
    //                                         getCustomFieldType(itemValues.customFieldId) === inputTypesDef.dropdown &&

    //                                         RenderCustomFieldSelectionDropdown({
    //                                             index,
    //                                             itemValues,
    //                                             itemErrors,
    //                                             itemTouched
    //                                         })
    //                                     }


    //                                     {
    //                                         getCustomFieldType(itemValues.customFieldId) === inputTypesDef.multidropdown &&

    //                                         RenderCustomFieldSelectionMultiDropdown({
    //                                             index,
    //                                             itemValues,
    //                                             itemErrors,
    //                                             itemTouched
    //                                         })
    //                                     }

    //                                 </Grid>
    //                             </Grid>
    //                         </ListItem>
    //                     )

    //                 })
    //             }
    //             </List>

    //         </Grid>
    //     </>
    //     )
    // }

    const getCustomFieldType = (id) => {

        if (!id) return ""

        const a = customFields.filter(i => i.id === id)
        return a ? a[0].field_type : ""
    }

    const getCustomFieldOptions = (id) => {

        if (!id) return []

        const a = customFields.filter(i => i.id === id)
        return a ? a[0].options : []
    }

    const getCustomFieldOptionKeyByValue = (valueArray) => {

        if (!valueArray) return []

        // customFields.filter(i => i.id === id)

        let optionKeys = [];

        customFields.forEach((e) => {
            e.options.forEach((e2) => {
                if (valueArray.includes(e2.option_name)) {
                    optionKeys.push(e2.option_key)
                }
            })
        })

        return optionKeys
    }

    // const getCustomFieldOptionNameByIds = (optionIdArray) => {

    //     if(!optionIdArray) return []

    //     customFields.filter(i => i.id === id)

    //     let optionNames = [];

    //     customFields.forEach((e) => {
    //         e.options.forEach((e2) => {
    //             // if(e2.option_key === optionIdArray){
    //             if(optionIdArray.includes(e2.option_key)){
    //                 optionNames.push(e2.option_name)
    //             }
    //         })
    //     })

    //     return optionNames
    // }

    const getSupplierOptionNameByIds = (optionIdArray) => {

        if (!optionIdArray) return []

        dataSuppliers.filter(i => i.id === id)

        let optionNames = [];

        dataSuppliers.forEach((e) => {
            // e.options.forEach((e2) => {
            // if(e2.option_key === optionIdArray){
            if (optionIdArray.includes(e.id)) {
                optionNames.push(e.company_name)
            }
            // })
        })

        return optionNames
    }

    const getUom = (id) => {

        if (!id) return ""

        const a = dataUoms.filter(i => i.id === id)
        return a ? a[0].name : ""
    }

    const RenderTransformRatio = () => {

        if (!dataProduct) return <></>
        if (!dataItem) return <></>

        // check this item is default
        const {
            default_uom_item_id: defaultItemId
        } = dataProduct

        const {
            uom_name: defaultUomName
        } = dataItem

        const isDefault = (id === defaultItemId)

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title='Transform Uom'
                        subTitle={"Conversion ratio between Uoms"}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    {/* {JSON.stringify(values)} */}
                    {
                        isDefault ?
                            (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3} sm={4}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            fullWidth
                                            name={"transformRatio"}
                                            label="Transform Ratio"
                                            type="text"
                                            variant="outlined"
                                            value={values.transformRatio}
                                            // onChange={handleChange}
                                            disabled
                                        // error={touched.transformRatio && Boolean(errors.transformRatio)}
                                        // helperText={touched.transformRatio && errors.transformRatio}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={9} sm={8} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                        <Chip
                                            label={`Marked as Default Uom, Transform ratio must be 1`}
                                            variant='contained'
                                            size="large"
                                            color={"warning"}
                                        />
                                    </Grid>
                                </Grid>
                            )
                            :
                            (
                                <Grid container spacing={1}>
                                    <Grid item xs={6} md={3} sm={6} display={"flex"} justifyContent={"center"} alignItems={"center"}>


                                        <TextField
                                            margin="normal"
                                            size="small"
                                            fullWidth
                                            // name={"transformRatio"}
                                            label="Current"
                                            type="text"
                                            variant="outlined"
                                            value={`1`}
                                            // onChange={handleChange}
                                            // error={touched.transformRatio && Boolean(errors.transformRatio)}
                                            // helperText={touched.transformRatio && errors.transformRatio}
                                            InputProps={{
                                                endAdornment:
                                                    <InputAdornment
                                                        position="end"
                                                    >
                                                        / {getUom(values?.uomId)}
                                                    </InputAdornment>
                                            }}
                                            disabled
                                        />

                                    </Grid>
                                    <Grid item xs={6} md={1} sm={6} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                        <CommonIcons name={icons.equal} sx={{ width: 25, height: 25 }} />
                                    </Grid>
                                    <Grid item xs={6} md={3} sm={6}>
                                        <TextField
                                            margin="normal"
                                            size="small"
                                            fullWidth
                                            name={"transformRatio"}
                                            label="Conversion"
                                            type="text"
                                            variant="outlined"
                                            value={values.transformRatio}
                                            onChange={handleChange}
                                            error={touched.transformRatio && Boolean(errors.transformRatio)}
                                            helperText={touched.transformRatio && errors.transformRatio}
                                            InputProps={{
                                                endAdornment:
                                                    <InputAdornment
                                                        position="end"
                                                    >
                                                        / {defaultUomName}
                                                    </InputAdornment>
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            )
                    }

                </Grid>
            </>
        )
    }

    const RenderTresholdControl = () => {
        return (

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title='Threshold Control'
                        subTitle={"Set qty of threshold level"}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={12} md={3} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                            <Chip
                                label={"Reorder"}
                                color='primary'
                                variant='outlined'
                                size='large'
                                sx={{
                                    width: '100%',
                                    // textAlign: 'left',
                                    fontSize: 16,
                                    fontWeight: 800
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"reorderThresholdQty"}
                                label="Threshold Qty"
                                type="text"
                                variant="outlined"
                                value={values.reorderThresholdQty}
                                onChange={handleChange}
                                error={touched.reorderThresholdQty && Boolean(errors.reorderThresholdQty)}
                                helperText={touched.reorderThresholdQty && errors.reorderThresholdQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Action perform when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"reorderTopUpQty"}
                                label="Topup Qty"
                                type="text"
                                variant="outlined"
                                value={values.reorderTopUpQty}
                                onChange={handleChange}
                                error={touched.reorderTopUpQty && Boolean(errors.reorderTopUpQty)}
                                helperText={touched.reorderTopUpQty && errors.reorderTopUpQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Suggestion qty to topup when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                            <Chip
                                label={"Relocate"}
                                color='primary'
                                variant='outlined'
                                size='large'
                                sx={{
                                    width: '100%',
                                    // textAlign: 'left',
                                    fontSize: 16,
                                    fontWeight: 800
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"relocateThresholdQty"}
                                label="Threshold Qty"
                                type="text"
                                variant="outlined"
                                value={values.relocateThresholdQty}
                                onChange={handleChange}
                                error={touched.relocateThresholdQty && Boolean(errors.relocateThresholdQty)}
                                helperText={touched.relocateThresholdQty && errors.relocateThresholdQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Action perform when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"relocateTopUpQty"}
                                label="Topup Qty"
                                type="text"
                                variant="outlined"
                                value={values.relocateTopUpQty}
                                onChange={handleChange}
                                error={touched.relocateTopUpQty && Boolean(errors.relocateTopUpQty)}
                                helperText={touched.relocateTopUpQty && errors.relocateTopUpQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Suggestion qty to topup when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (
            <Box
                sx={{
                    // p: 3,
                    // pt: 0,
                    // maxHeight: 600,
                }}
            >
                <Grid container>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title='Information'
                            subTitle={"Provide details of the profile"}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        <Grid container spacing={1}>

                            {/* <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={touched.uomId && Boolean(errors.uomId)}
                                >
                                    <InputLabel id="uomId">UOM</InputLabel>
                                        <Select
                                            labelId="uomId"
                                            label="UOM"
                                            name={"uomId"}
                                            onChange={handleChange}
                                            value={
                                                checkValueExists({ 
                                                    searchFieldId: "id",
                                                    dataSearch: dataUoms, 
                                                    valueToCompare: values?.uomId 
                                                })
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                dataUoms && dataUoms.map((item, index) => 
                                                    <MenuItem key={index} value={item?.id}>{item.name}</MenuItem>
                                                )
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.uomId && errors.uomId}
                                        </FormHelperText>
                                </FormControl>
                            </Grid> */}

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"uomId"}
                                    label="Uom"
                                    type="text"
                                    variant="outlined"
                                    value={getUom(values?.uomId)}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} sm={6} md={3}
                                sx={{
                                    display: 'center',
                                }}>
                                <FormControlLabel
                                    sx={{
                                        marginLeft: '1px',
                                        my: 1
                                    }}
                                    control={
                                        <Switch
                                            name="uomStatus"
                                            checked={Boolean(values.uomStatus)}
                                            onChange={(event, checked) => {
                                                setFieldValue("uomStatus", checked);
                                            }}
                                        />
                                    }
                                    label={
                                        Boolean(values?.uomStatus) ? "Enabled" : "Disabled"
                                    }
                                />
                            </Grid>

                            {/* <Grid item xs={6} sm={6} md={3}
                                sx={{
                                    display:'center',
                                }}>
                                <FormControlLabel 
                                    sx={{
                                        ml: 1,
                                    }}
                                    control={
                                        <Switch 
                                            name="packageStatus" 
                                            checked={Boolean(values?.packageStatus)}
                                            onChange={(event, checked) => {
                                                setFieldValue("packageStatus", checked);
                                            }}
                                        />
                                    }     
                                    label={
                                        Boolean(values?.packageStatus)? "Is Package" : "Not Package"
                                    }
                                />
                            </Grid> */}

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"code"}
                                    label="Code"
                                    type="text"
                                    variant="outlined"
                                    value={values?.code}
                                    // onChange={handleChange}
                                    // error={touched.code && Boolean(errors.code)}
                                    // helperText={touched.code && errors.code}
                                    disabled
                                    InputProps={{
                                        sx: {
                                            pr: 1
                                        },
                                        endAdornment:
                                            <InputAdornment
                                                position="end"
                                            >
                                                <Tooltip
                                                    title={`Unique Identifier`}
                                                >
                                                    <IconButton>
                                                        <CommonIcons name={icons.info} />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                    }}
                                />
                            </Grid>

                            {/* <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"displayCode"}
                                    label="Display Code"
                                    type="text"                                         
                                    variant="outlined"
                                    value={values?.displayCode}
                                    onChange={handleChange}
                                    error={touched.displayCode && Boolean(errors.displayCode)}
                                    helperText={touched.displayCode && errors.displayCode}
                                />
                            </Grid> */}

                            <Grid item xs={12} sm={12} md={6}>

                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={touched.supplierId && Boolean(errors.supplierId)}
                                >
                                    <InputLabel id="supplierId">Supplier</InputLabel>
                                    <Select
                                        labelId="supplierId"
                                        label="Supplier"
                                        name={"supplierId"}
                                        // onChange={(event) =>{
                                        //         setFieldValue("supplierId",event.target.value)
                                        //     }
                                        // }
                                        onChange={handleChange}
                                        value={values?.supplierId ? values.supplierId : []}
                                        renderValue={(selected) => {

                                            return (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {
                                                        getSupplierOptionNameByIds(selected).map((i, index) => (
                                                            <Chip
                                                                key={index}
                                                                // color={'red'}
                                                                label={spliceString({ val: i, maxLength: 20 })}
                                                            />
                                                        ))
                                                    }
                                                </Box>
                                            )

                                        }}
                                        size='small'
                                        margin='dense'
                                        multiple
                                        input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                    >
                                        {/* {
                                            dataSuppliers && dataSuppliers.map((item, index) => 
                                                <MenuItem key={index} value={item?.id}>{item.company_name}</MenuItem>
                                            )
                                        } */}

                                        {
                                            dataSuppliers?.map((i, index) => {
                                                return (
                                                    <MenuItem
                                                        key={index}
                                                        value={i?.id}
                                                    >
                                                        <Checkbox checked={values.supplierId.indexOf(i?.id) > -1} />
                                                        {/* <ListItemText 
                                                            primary={`${i.company_name}`} 
                                                        /> */}
                                                        <ListItemText primary={`${spliceString({ val: i.company_name, maxLength: 50 })}`} />

                                                    </MenuItem>
                                                )
                                            })
                                        }

                                    </Select>

                                    <FormHelperText>
                                        {touched.supplierId && errors.supplierId}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"uomDesc"}
                                    label="Description"
                                    type="text"
                                    variant="outlined"
                                    value={values?.uomDesc ? values.uomDesc : ""}
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={touched.uomDesc && Boolean(errors.uomDesc)}
                                    helperText={touched.uomDesc && errors.uomDesc}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label="Remark"
                                    type="text"
                                    variant="outlined"
                                    value={values?.remark ? values.remark : ""}
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repackRemark"}
                                    label="Repack Remark"
                                    type="text"
                                    variant="outlined"
                                    value={values?.repackRemark ? values.repackRemark : ""}
                                    multiline
                                    rows={3}
                                    onChange={handleChange}
                                    error={touched.repackRemark && Boolean(errors.repackRemark)}
                                    helperText={touched.repackRemark && errors.repackRemark}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ mt: 2, mb: 1 }} />
                    </Grid>

                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title='Measurement &amp; Pricing'
                            subTitle={"Provide detail item information"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={9}>
                        <Grid container spacing={1}>

                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"cost"}
                                    label="Cost Price"
                                    type="number"
                                    variant="outlined"
                                    value={values?.cost}
                                    onChange={handleChange}
                                    error={touched.cost && Boolean(errors.cost)}
                                    helperText={touched.cost && errors.cost}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"uomPrice"}
                                    label="UOM Price"
                                    type="number"
                                    variant="outlined"
                                    value={values?.uomPrice}
                                    onChange={handleChange}
                                    error={touched.uomPrice && Boolean(errors.uomPrice)}
                                    helperText={touched.uomPrice && errors.uomPrice}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"floorPrice"}
                                    label="Floor Price"
                                    type="number"
                                    variant="outlined"
                                    value={values?.floorPrice}
                                    onChange={handleChange}
                                    error={touched.floorPrice && Boolean(errors.floorPrice)}
                                    helperText={touched.floorPrice && errors.floorPrice}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"volumeWeight"}
                                    label="Volume Weight"
                                    type="text"
                                    variant="outlined"
                                    value={values?.volumeWeight}
                                    onChange={handleChange}
                                    error={touched.volumeWeight && Boolean(errors.volumeWeight)}
                                    helperText={touched.volumeWeight && errors.volumeWeight}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"grossWeight"}
                                    label="Gross Weight"
                                    type="number"
                                    variant="outlined"
                                    value={values?.grossWeight}
                                    onChange={handleChange}
                                    error={touched.grossWeight && Boolean(errors.grossWeight)}
                                    helperText={touched.grossWeight && errors.grossWeight}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    name={"netWeight"}
                                    label="Net Weight"
                                    type="number"
                                    variant="outlined"
                                    value={values?.netWeight}
                                    onChange={handleChange}
                                    error={touched.netWeight && Boolean(errors.netWeight)}
                                    helperText={touched.netWeight && errors.netWeight}
                                />
                            </Grid>

                            <Grid
                                container
                                spacing={1}
                                sx={{
                                    display: 'flex'
                                }}
                            >
                                <Grid item xs={4} sm={4} md={2}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"width"}
                                        label="Width"
                                        type="text"
                                        variant="outlined"
                                        value={values?.width}
                                        onChange={handleChange}
                                        error={touched.width && Boolean(errors.width)}
                                        helperText={touched.width && errors.width}
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment
                                                    position="end"
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 800,
                                                            color: 'black'
                                                        }}
                                                    >W</Typography>
                                                </InputAdornment>
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4} md={2}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"height"}
                                        label="Height"
                                        type="text"
                                        variant="outlined"
                                        value={values?.height}
                                        onChange={handleChange}
                                        error={touched.height && Boolean(errors.height)}
                                        helperText={touched.height && errors.height}
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment
                                                    position="end"
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 800,
                                                            color: 'black'
                                                        }}
                                                    >H</Typography>
                                                </InputAdornment>
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4} md={2}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"length"}
                                        label="Length"
                                        type="text"
                                        variant="outlined"
                                        value={values?.length}
                                        onChange={handleChange}
                                        error={touched.length && Boolean(errors.length)}
                                        helperText={touched.length && errors.length}
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment
                                                    position="end"
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 800,
                                                            color: 'black'
                                                        }}
                                                    >L</Typography>
                                                </InputAdornment>
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"cbm"}
                                        label="CBM"
                                        type="text"
                                        variant="outlined"
                                        value={values?.cbm}
                                        onChange={handleChange}
                                        error={touched.cbm && Boolean(errors.cbm)}
                                        helperText={touched.cbm && errors.cbm}
                                    />
                                </Grid>
                                {/* <Grid item xs={6} md={3} sm={6}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"transformRatio"}
                                        label="Transform Ratio"
                                        type="text"                                         
                                        variant="outlined"
                                        value={values.transformRatio}
                                        onChange={handleChange}
                                        error={touched.transformRatio && Boolean(errors.transformRatio)}
                                        helperText={touched.transformRatio && errors.transformRatio}
                                    />
                                </Grid>  */}
                            </Grid>

                        </Grid>
                    </Grid>

                    {
                        RenderTresholdControl()
                    }

                    {
                        RenderTransformRatio()
                    }

                    {
                        RenderCustomFieldSelection()
                    }

                </Grid>
            </Box>
        )
    }

    const RenderMain = () => {
        return (

            <form
                onSubmit={handleSubmit}
                style={{
                    height: '100%'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        p: 3
                        // alignItems: 'center'
                    }}
                >
                    <Box>
                        {
                            RenderForm()
                        }
                    </Box>

                    <Box
                        sx={{
                            my: 2,
                            mb: 2,
                            textAlign: 'right'
                        }}
                    >
                        <Button
                            variant='contained'
                            // color='inherit'
                            type='submit'
                        >
                            Save Changes
                        </Button>
                    </Box>

                </Box>

            </form>


        )
    }

    return (
        <Box
            sx={{
                height: '100%'
            }}
        >

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderMain()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}